import {UIService} from "../UIService";
import {ProductDataService} from "../../services/productDataService";
import {ProductDataStore} from "../../stores/productDataStore";
import {MullionsService} from "./mullionsService";
import {AppStateService} from "../../services/appStateService";
import {EventService} from "../../services/eventService";
import {PvcWindowMullionRule} from "../../services/pvcWindowMullionRule";
import filter from "lodash/filter";

export class MullionsUIService {
    private uiService: UIService;
    private productDataService: ProductDataService;
    private productDataStore: ProductDataStore;
    private mullionsService: MullionsService;
    private appStateService: AppStateService;
    private eventService: EventService;
    private pvcWindowMullionRule: PvcWindowMullionRule
    private diagonalMuntinRuleActive = false;

    constructor() {
        this.uiService = new UIService();
        this.productDataService = new ProductDataService();
        this.productDataStore = new ProductDataStore();
        this.mullionsService = new MullionsService();
        this.appStateService = new AppStateService();
        this.eventService = new EventService();
        this.pvcWindowMullionRule = new PvcWindowMullionRule();

        this.eventService.getTarget().addEventListener('colorChange', (data) => {
            this.setMuntinColorBasedOnConstructionColor();
        });

        this.eventService.getTarget().addEventListener('sizeChange', (data) => {
            this.refreshPredefinedCombinations();
            this.checkPvcWindowMullionRule();
        });
        this.populatePreview();
    }

    private populatePreview()
    {
        const selectedPredefinedSetup = this.productDataStore.getSelectedOptionData('predefinedMullionLayout');
        const selectedCustomPredefinedSetupNameElements = this.uiService.getAllElementsByValueKeyAttribute('mullionsSelectedCustomPredefinedSetupName');

        if (selectedPredefinedSetup === null) {
            selectedCustomPredefinedSetupNameElements.forEach((element) => {
                this.uiService.replaceTextInElement(
                    element,
                    ''
                );
            });
            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsDefaultPredefinedSetupSelected', true);
            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsCustomPredefinedSetupSelected', false);
            this.uiService.refreshElementsInModule('mullions');
            return;
        }
        selectedCustomPredefinedSetupNameElements.forEach((element) => {
            this.uiService.replaceTextInElement(
                element,
                selectedPredefinedSetup.attributes.name
            );
        });
        this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsDefaultPredefinedSetupSelected', false);
        this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsCustomPredefinedSetupSelected', true);
        this.uiService.refreshElementsInModule('mullions');
    }

    public initializeMullionsSelector()
    {
        this.refreshPredefinedCombinations();
        this.initializeMullionConfigurationTypeSelector();
        this.initializeMullionAndMuntinLineDescription();
        this.initializeMullionTypeSelector();
        this.initializeMullionSelector();
        this.initializeCartMullionLayoutsSelector();
        this.initializeHideDescriptionButton();

        // const resetButton = this.uiService.getResetButtonByModule('mullion');
        // resetButton.addEventListener('click', this.resetSelectedOptions.bind(this));
    }

    private refreshPredefinedCombinations()
    {
        if (this.productDataStore.getProductShape() !== 'triangle') {
            this.uiService.removeElementsByPartName('mullionsPredefinedSetup');
            let predefinedCombinations = this.mullionsService.getPredefinedCombinations();
            const selectedPredefinedSetup = this.productDataStore.getSelectedOptionData('predefinedMullionLayout');

            predefinedCombinations.forEach(combination => {
                const option = this.createPredefinedMullionOption(combination);
                if (selectedPredefinedSetup !== null && selectedPredefinedSetup.id !== undefined) {
                    if (selectedPredefinedSetup.id === combination.id) {
                        this.validateDiagonalMuntinRule(combination);
                        this.resetSelectedOptions();
                        this.productDataService.updatePredefinedMullionLayout(combination);
                        this.uiService.setSelectedOption(option);
                        this.populatePreview();
                    }
                }
                option.addEventListener('click', (event: any) => {
                    this.validateDiagonalMuntinRule(combination);
                    this.resetSelectedOptions();
                    this.productDataService.updatePredefinedMullionLayout(combination);
                    this.uiService.setSelectedOption(event.currentTarget);
                    this.populatePreview();
                });
            });
        }
    }

    private resetSelectedOptions()
    {
        this.mullionsService.resetSelectedOptions();

        const horizontalMullionInput = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMullionInput');
        const verticalMullionInput = this.uiService.getElementByAttribute('nf-template-action', 'verticalMullionInput');
        const horizontalMuntinInput = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMuntinInput');
        const verticalMuntinInput = this.uiService.getElementByAttribute('nf-template-action', 'verticalMuntinInput');
        const cartPredefinedMullionSetupsSelector: any = this.uiService.getElementByAttribute('nf-template-action', 'selectCartPredefinedMullionSetup');

        this.updateQuantityInput(horizontalMullionInput, 0);
        this.updateQuantityInput(verticalMullionInput, 0);
        this.updateQuantityInput(horizontalMuntinInput, 0);
        this.updateQuantityInput(verticalMuntinInput, 0);
        cartPredefinedMullionSetupsSelector.value = 0;

        this.eventService.emit('reloadDrawing');
    }

    private initializeMullionConfigurationTypeSelector()
    {
        this.uiService.getElementByAttribute('nf-template-action', 'mullionsDefaultConfigurations').addEventListener('click', (event: any) => {
            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsCustomConfigurationsContainer', false);
            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsDefaultConfigurationsContainer', true);
            this.uiService.refreshElementsInModule('mullions');

            this.uiService.resetSelectionsByPartName('mullionsConfigurationType');
            this.uiService.setSelectedOption(event.target);
        });

        this.uiService.getElementByAttribute('nf-template-action', 'mullionsCustomConfigurations').addEventListener('click', (event: any) => {
            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsCustomConfigurationsContainer', true);
            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionsDefaultConfigurationsContainer', false);
            this.uiService.refreshElementsInModule('mullions');

            this.uiService.resetSelectionsByPartName('mullionsConfigurationType');
            this.uiService.setSelectedOption(event.target);
        });
    }

    private initializeHideDescriptionButton(): void
    {
        const hideDescriptionButton = this.uiService.getElementByAttribute('nf-template-action', 'hideMullionDescription');
        const mullionDescriptionContainer = this.uiService.getElementByAttribute('nf-template-element', 'mullionDescriptionContainer');

        hideDescriptionButton.addEventListener('click', () => {
            mullionDescriptionContainer.classList.toggle('--show');
        });
    }

    private initializeMullionTypeSelector()
    {
        this.hideMuntinOptions();

        const mullionsGroupContainer = this.uiService.getElementByAttribute('nf-template-element', 'mullionsGroupContainer');
        const muntinsGroupContainer = this.uiService.getElementByAttribute('nf-template-element', 'muntinsGroupContainer');

        const mullionTypeConfiguratorBtn = this.uiService.getElementByAttribute('nf-template-action', 'mullionTypeConfigurator');
        const muntinMiddleglassConfiguratorBtn = this.uiService.getElementByAttribute('nf-template-action', 'muntinMiddleglassTypeConfigurator');
        const muntinExternalConfiguratorBtn = this.uiService.getElementByAttribute('nf-template-action', 'muntinExternalTypeConfigurator');
        const muntinBothSideConfiguratorBtn = this.uiService.getElementByAttribute('nf-template-action', 'muntinBothSideTypeConfigurator');

        if (this.mullionsService.hideMullionOptionOnRule()) {
            this.uiService.disableOption(
                this.uiService.getElementByAttribute('nf-template-action', 'mullionTypeConfigurator')
            );

            if (!this.uiService.isHiddenElement(muntinMiddleglassConfiguratorBtn)) {
                this.selectMuntinOption(muntinMiddleglassConfiguratorBtn, 'middleglass');
            } else if (!this.uiService.isHiddenElement(muntinExternalConfiguratorBtn)) {
                this.selectMuntinOption(muntinExternalConfiguratorBtn, 'external');
            } else if (!this.uiService.isHiddenElement(muntinBothSideConfiguratorBtn)) {
                this.selectMuntinOption(muntinBothSideConfiguratorBtn, 'in_out_duplex');
            }
        } else {
            mullionTypeConfiguratorBtn.addEventListener('click', (event: any) => {
                this.uiService.resetSelectionsByPartName('mullionsCustomConfiguration');
                this.uiService.resetSelection([muntinsGroupContainer]);
                this.uiService.setSelectedOption(mullionTypeConfiguratorBtn);
                this.uiService.setSelectedOption(mullionsGroupContainer);

                this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionFieldsContainer', true);
                this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinFieldsContainer', false);
                this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionDescription', true);
                this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinDescription', false);
                this.uiService.refreshElementsInModule('mullions');
            });
        }

        muntinMiddleglassConfiguratorBtn.addEventListener('click', (event: any) => {
            this.selectMuntinOption(muntinMiddleglassConfiguratorBtn, 'middleglass');
        });

        muntinExternalConfiguratorBtn.addEventListener('click', (event: any) => {
            if (this.diagonalMuntinRuleActive) {
                return;
            }

            this.selectMuntinOption(muntinExternalConfiguratorBtn, 'external');
        });

        muntinBothSideConfiguratorBtn.addEventListener('click', (event: any) => {
            if (this.diagonalMuntinRuleActive) {
                return;
            }

            this.selectMuntinOption(muntinBothSideConfiguratorBtn, 'in_out_duplex');
        });

        this.initializeMuntinColorSelector();
    }

    private selectMuntinOption(muntinOption: Element, muntinType: string) {
        const mullionsGroupContainer = this.uiService.getElementByAttribute('nf-template-element', 'mullionsGroupContainer');
        const muntinsGroupContainer = this.uiService.getElementByAttribute('nf-template-element', 'muntinsGroupContainer');

        this.uiService.resetSelectionsByPartName('mullionsCustomConfiguration');
        this.uiService.resetSelection([mullionsGroupContainer]);
        this.uiService.setSelectedOption(muntinOption);
        this.uiService.setSelectedOption(muntinsGroupContainer);

        this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionFieldsContainer', false);
        this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinFieldsContainer', true);
        this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionDescription', false);
        this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinDescription', true);
        this.uiService.refreshElementsInModule('mullions');

        this.setMuntinColorsInSelectors(muntinType);
        this.initializeMuntinSelector(muntinType);
    }

    private setMuntinColorsInSelectors(type: string)
    {
        const selectedMuntinSizeId = this.productDataStore.getSelectedOptionIdByName('mullionSize');
        const selectedMuntinSize = this.productDataStore.getAvailableOptionByNameAndId('mullionSize', selectedMuntinSizeId);

        const insideColors = (type === 'external') ? [] : this.mullionsService.getInsideMuntinColors(selectedMuntinSize.attributes.insideColourPaletteId);
        const outsideColors = this.mullionsService.getOutsideMuntinColors(selectedMuntinSize.attributes.outsideColourPaletteId);

        if (type !== 'external') {
            this.uiService.replaceSelectOptions(
                this.uiService.getElementByAttribute('nf-template-action', 'muntinInsideColorSelect'),
                insideColors,
                this.productDataStore.getSelectedOptionIdByName('mullionColorInner') ?? null
            );

            this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinInsideColor', true);
            this.uiService.refreshElementsInModule('mullions');
        } else {
            this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinInsideColor', false);
            this.uiService.refreshElementsInModule('mullions');
        }

        this.uiService.replaceSelectOptions(
            this.uiService.getElementByAttribute('nf-template-action', 'muntinOutsideColorSelect'),
            outsideColors,
            this.productDataStore.getSelectedOptionIdByName('mullionColorOuter') ?? null
        );
    }

    private initializeMuntinColorSelector()
    {
        const innerMuntinColorSelector: any = this.uiService.getElementByAttribute('nf-template-action', 'muntinInsideColorSelect');
        const outerMuntinColorSelector: any = this.uiService.getElementByAttribute('nf-template-action', 'muntinOutsideColorSelect');

        innerMuntinColorSelector.addEventListener('change', (event: any) => {
            this.productDataService.updateSelectedOptionByName('mullionColorInner', innerMuntinColorSelector.value, true, true);
        });

        outerMuntinColorSelector.addEventListener('change', (event: any) => {
            this.productDataService.updateSelectedOptionByName('mullionColorOuter', outerMuntinColorSelector.value, true, true);
        });
    }

    private hideMuntinOptions()
    {
        let muntins = this.productDataStore.getAvailableOptionsByName('mullionSize');
        let middleglassMuntins = muntins.filter(muntin => muntin.attributes.barType === 'middleglass');
        let externalMuntins = muntins.filter(muntin => muntin.attributes.barType === 'external');
        let bothSideMuntins = muntins.filter(muntin => muntin.attributes.barType === 'in_out_duplex');

        if (middleglassMuntins.length <= 0) {
            this.uiService.hideElement(
                this.uiService.getElementByAttribute('nf-template-action', 'muntinMiddleglassTypeConfigurator')
            );
        }

        if (externalMuntins.length <= 0) {
            this.uiService.hideElement(
                this.uiService.getElementByAttribute('nf-template-action', 'muntinExternalTypeConfigurator')
            );
        }

        if (bothSideMuntins.length <= 0) {
            this.uiService.hideElement(
                this.uiService.getElementByAttribute('nf-template-action', 'muntinBothSideTypeConfigurator')
            );
        }
    }

    private initializeMullionAndMuntinLineDescription()
    {
        let muntinLineDescription = this.productDataStore.getProductInfoAttributeByName('lineMuntinsDescription');
        let mullionLineDescription = this.productDataStore.getProductInfoAttributeByName('lineMullionsDescription');

        this.uiService.addTextInElement(
            this.uiService.getElementByAttribute('nf-template-element', 'muntinLineDescription'),
            muntinLineDescription
        );

        this.uiService.addTextInElement(
            this.uiService.getElementByAttribute('nf-template-element', 'mullionLineDescription'),
            mullionLineDescription
        );
    }

    private initializeMullionSelector()
    {
        let mullions = this.productDataStore.getAvailableOptionsByName('dividerSize');

        mullions.forEach(mullion => {
            const option = this.createMullionOption(mullion);

            if (
                mullion.id === this.productDataStore.getSelectedOptionIdByName('dividerSize') &&
                !(this.mullionsService.validatePvcWindowMullionRule() && mullion.attributes.size === this.pvcWindowMullionRule.getMullionSizeRestriction())
            ) {
                if (mullion.attributes.description !== '') {
                    this.updateMullionDescription(mullion.attributes.description);
                }

                this.uiService.setSelectedOption(option);
            }

            option.addEventListener('click', (event: any) => {
                if (mullion.attributes.description !== '') {
                    this.updateMullionDescription(mullion.attributes.description);
                }
                this.uiService.resetSelectionsByPartName('mullion');
                this.uiService.setSelectedOption(event.currentTarget);
                this.productDataService.updateSelectedOptionByName('dividerSize', mullion.id, true);
            });
        });

        const imageGallery: any = this.uiService.getElementByClassName('configurator__mullion-image-gallery');
        // @ts-ignore
        window.lightGallery(imageGallery, {
            licenseKey: 'B1649D2E-93694BB8-82AC0A28-8721BA58',
            download: false,
            autoplayFirstVideo: false,
            animateThumb: false,
            currentPagerPosition: 'right',
            selector: '.configurator__mullion-gallery-image'
        });

        const initialHorizontalMullionQuantity = this.productDataStore.getAttributeByName('mullions').horizontalCount ?? this.productDataStore.getAttributeByName('dividersHorizontal');
        const initialVerticalMullionQuantity = this.productDataStore.getAttributeByName('mullions').verticalCount ?? this.productDataStore.getAttributeByName('dividersVertical');
        const initialHorizontalMuntinQuantity = this.productDataStore.getAttributeByName('muntins').horizontalCount ?? 0;
        const initialVerticalMuntinQuantity = this.productDataStore.getAttributeByName('muntins').verticalCount ?? 0;

        const horizontalMullionInput: any = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMullionInput');
        horizontalMullionInput.value = initialHorizontalMullionQuantity;
        const verticalMullionInput: any = this.uiService.getElementByAttribute('nf-template-action', 'verticalMullionInput');
        verticalMullionInput.value = initialVerticalMullionQuantity;

        this.uiService.getElementByAttribute('nf-template-action', 'removeHorizontalMullionButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let horizontalMullions = this.productDataService.removeMullion('horizontal');
            this.updateQuantityInput(horizontalMullionInput, horizontalMullions);
        });
        this.uiService.getElementByAttribute('nf-template-action', 'addHorizontalMullionButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let horizontalMullions = this.productDataService.addMullion('horizontal');
            this.updateQuantityInput(horizontalMullionInput, horizontalMullions);
        });
        this.uiService.getElementByAttribute('nf-template-action', 'removeVerticalMullionButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let verticalMullions = this.productDataService.removeMullion('vertical');
            this.updateQuantityInput(verticalMullionInput, verticalMullions);
        });
        this.uiService.getElementByAttribute('nf-template-action', 'addVerticalMullionButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let verticalMullions = this.productDataService.addMullion('vertical');
            this.updateQuantityInput(verticalMullionInput, verticalMullions);
        });

        const horizontalMuntinInput: any = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMuntinInput');
        const verticalMuntinInput: any = this.uiService.getElementByAttribute('nf-template-action', 'verticalMuntinInput');
        horizontalMuntinInput.value = initialHorizontalMuntinQuantity;
        verticalMuntinInput.value = initialVerticalMuntinQuantity;

        this.uiService.getElementByAttribute('nf-template-action', 'removeHorizontalMuntinButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let horizontalMuntins = this.productDataService.removeMuntin('horizontal');
            this.updateQuantityInput(horizontalMuntinInput, horizontalMuntins);
        });

        this.uiService.getElementByAttribute('nf-template-action', 'addHorizontalMuntinButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let horizontalMuntins = this.productDataService.addMuntin('horizontal');
            this.updateQuantityInput(horizontalMuntinInput, horizontalMuntins);
        });

        this.uiService.getElementByAttribute('nf-template-action', 'removeVerticalMuntinButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let verticalMuntins = this.productDataService.removeMuntin('vertical');
            this.updateQuantityInput(verticalMuntinInput, verticalMuntins);
        });

        this.uiService.getElementByAttribute('nf-template-action', 'addVerticalMuntinButton').addEventListener('click', (event: any) => {
            if (this.productDataService.isPredefinedMullionsLayoutSelected()) {
                this.productDataService.removePredefinedMullionsLayout();
            }
            let verticalMuntins = this.productDataService.addMuntin('vertical');
            this.updateQuantityInput(verticalMuntinInput,verticalMuntins);
        });
    }

    private initializeCartMullionLayoutsSelector()
    {
        const predefinedLayouts = this.productDataStore.getAvailableOptionsByName('mullionLayoutsInCart');

        if (predefinedLayouts.length === 0) {
            return;
        }
        const cartPredefinedMullionSetupsSelector: any = this.uiService.getElementByAttribute('nf-template-action', 'selectCartPredefinedMullionSetup');
        const selectorData = [{
            name: '',
            value: 0
        }];
        predefinedLayouts.forEach(layout => {
            selectorData.push({
                name: layout.name,
                value: layout.id,
            });
        });
        this.uiService.replaceSelectOptions(
            cartPredefinedMullionSetupsSelector,
            selectorData
        );

        cartPredefinedMullionSetupsSelector.addEventListener('change', (event: any) => {
            const selectedValue = cartPredefinedMullionSetupsSelector.value;

            this.resetSelectedOptions();
            if (selectedValue === 0) {
                return;
            }

            this.productDataService.applyCartMullionLayout(parseInt(selectedValue));
        });
        this.appStateService.setAppSettingTemplateFlag('mullions', 'showMullionAlignment', true);
        this.uiService.refreshElementsInModule('mullions');
    }

    private initializeMuntinSelector(type: string, setDefaultOption = false)
    {
        let muntins = this.productDataStore.getAvailableOptionsByName('mullionSize');
        muntins = muntins.filter(muntin => muntin.attributes.barType === type);
        this.uiService.removeElementsByPartName('muntin');

        muntins.forEach((muntin, index) => {
            const option = this.createMuntinOption(muntin);

            if (
                (muntin.id === this.productDataStore.getSelectedOptionIdByName('mullionSize') && !setDefaultOption) ||
                (
                    index === 0 && setDefaultOption
                )
            ) {
                if (muntin.attributes.description !== '') {
                    this.updateMuntinDescription(muntin.attributes.description);
                }
                this.productDataService.updateSelectedOptionByName('mullionSize', muntin.id, true);
                this.uiService.setSelectedOption(option);
            }

            option.addEventListener('click', (event: any) => {
                if (muntin.attributes.description !== '') {
                    this.updateMuntinDescription(muntin.attributes.description);
                }
                this.uiService.resetSelectionsByPartName('muntin');
                this.uiService.setSelectedOption(event.currentTarget);
                this.productDataService.updateSelectedOptionByName('mullionSize', muntin.id, true, true);
            });
        });
    }

    private createPredefinedMullionOption(combination)
    {
        const option = this.uiService.createConfiguratorOption('mullionsPredefinedSetup');

        this.uiService.setAttributeToElement(
            this.uiService.getElementByAttribute('nf-value-key', 'img', option),
            'src',
            combination.attributes.imgSrc
        );

        this.uiService.addTextInElement(
            this.uiService.getElementByAttribute('nf-value-key', 'title', option),
            combination.attributes.name
        );

        return option;
    }

    private createMullionOption(mullion)
    {
        const option = this.uiService.createConfiguratorOption('mullion');

        let mullionImage = (mullion.attributes.type === 'smooth') ? '/img/mittpost_slät.jpg' : '/img/mittpost_dekor.jpg';

        this.uiService.setAttributeToElement(
            option,
            'nf-option-id',
            mullion.id
        );

        this.uiService.setAttributeToElement(
            option,
            'nf-option-size',
            mullion.attributes.size
        );

        this.uiService.setAttributeToElement(
            this.uiService.getElementByAttribute('nf-value-key', 'img', option),
            'src',
            mullionImage
        );

        this.uiService.setAttributeToElement(
            this.uiService.getElementByAttribute('nf-value-key', 'imgLink', option),
            'href',
            mullionImage
        );

        this.uiService.addTextInElement(
            this.uiService.getElementByAttribute('nf-value-key', 'name', option),
            mullion.attributes.name
        );

        this.uiService.getElementByAttribute('nf-value-key', 'imgLink', option).classList.add('configurator__mullion-gallery-image');

        if (this.mullionsService.validatePvcWindowMullionRule() && mullion.attributes.size === this.pvcWindowMullionRule.getMullionSizeRestriction()) {
            this.uiService.disableOption(option);
        }

        return option;
    }

    private createMuntinOption(muntin)
    {
        const option = this.uiService.createConfiguratorOption('muntin');

        this.uiService.setAttributeToElement(
            option,
            'nf-option-id',
            muntin.id
        );

        this.uiService.setAttributeToElement(
            this.uiService.getElementByAttribute('nf-value-key', 'img', option),
            'src',
            muntin.attributes.img
        );

        this.uiService.addTextInElement(
            this.uiService.getElementByAttribute('nf-value-key', 'name', option),
            muntin.attributes.name
        );

        return option;
    }

    private updateMullionDescription(description: string): void
    {
        this.uiService.replaceTextInElement(
            this.uiService.getElementByAttribute('nf-template-flag', 'mullionDescription'),
            description
        );
    }

    private updateMuntinDescription(description: string): void
    {
        this.uiService.replaceTextInElement(
            this.uiService.getElementByAttribute('nf-template-flag', 'muntinDescription'),
            description
        );
    }

    private updateQuantityInput(input, value: number): void
    {
        input.value = value;
    }

    private validateDiagonalMuntinRule(combination: any) {
        if (combination.attributes.hasDiagonalMuntins) {
            this.diagonalMuntinRuleActive = true;
            this.appStateService.setAppSettingTemplateFlag('mullions', 'diagonalMuntinNotice', true);
            this.blockMuntinTypesBasedOnDiagonalRule();
        } else {
            this.diagonalMuntinRuleActive = false;
            this.appStateService.setAppSettingTemplateFlag('mullions', 'diagonalMuntinNotice', false);
            this.blockMuntinTypesBasedOnDiagonalRule(false);
        }
        this.uiService.refreshElementsInModule('mullions');
    }

    private blockMuntinTypesBasedOnDiagonalRule(block = true) {
        if (block) {
            const mullionsGroupContainer = this.uiService.getElementByAttribute('nf-template-element', 'mullionsGroupContainer');
            const muntinsGroupContainer = this.uiService.getElementByAttribute('nf-template-element', 'muntinsGroupContainer');
            const muntinMiddleglassConfiguratorBtn = this.uiService.getElementByAttribute('nf-template-action', 'muntinMiddleglassTypeConfigurator');
            const muntinExternalConfiguratorBtn = this.uiService.getElementByAttribute('nf-template-action', 'muntinExternalTypeConfigurator');
            const muntinBothSideConfiguratorBtn = this.uiService.getElementByAttribute('nf-template-action', 'muntinBothSideTypeConfigurator');

            muntinExternalConfiguratorBtn.classList.add('--disabled');
            muntinBothSideConfiguratorBtn.classList.add('--disabled');

            this.uiService.resetSelectionsByPartName('mullionsCustomConfiguration');
            this.uiService.resetSelection([mullionsGroupContainer]);
            this.uiService.setSelectedOption(muntinMiddleglassConfiguratorBtn);
            this.uiService.setSelectedOption(muntinsGroupContainer);

            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionFieldsContainer', false);
            this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinFieldsContainer', true);
            this.appStateService.setAppSettingTemplateFlag('mullions', 'mullionDescription', false);
            this.appStateService.setAppSettingTemplateFlag('mullions', 'muntinDescription', true);
            this.uiService.refreshElementsInModule('mullions');

            this.setMuntinColorsInSelectors('middleglass');
            this.initializeMuntinSelector('middleglass', true);
        } else {
            this.uiService.getElementByAttribute('nf-template-action', 'muntinExternalTypeConfigurator').classList.remove('--disabled');
            this.uiService.getElementByAttribute('nf-template-action', 'muntinBothSideTypeConfigurator').classList.remove('--disabled');
        }
    }

    private setMuntinColorBasedOnConstructionColor() {
        const selectedMuntinSize = this.productDataStore.getSelectedOptionData('mullionSize');

        if (!selectedMuntinSize) {
            return true;
        }
        const insideMuntinsColors = this.mullionsService.getInsideMuntinColors(selectedMuntinSize.attributes.insideColourPaletteId);
        const outsideMuntinsColors = this.mullionsService.getOutsideMuntinColors(selectedMuntinSize.attributes.outsideColourPaletteId);
        const constructionColor = this.productDataStore.getSelectedOption('outer', 'color');

        let defaultInsideColor = insideMuntinsColors.find(color => color.value === constructionColor.id);
        let defaultOutsideColor = outsideMuntinsColors.find(color => color.value === constructionColor.id);

        defaultInsideColor = (!defaultInsideColor) ? insideMuntinsColors.find(color => color.matchedColorId === constructionColor.id) : defaultInsideColor;
        defaultOutsideColor = (!defaultOutsideColor) ? outsideMuntinsColors.find(color => color.matchedColorId === constructionColor.id) : defaultOutsideColor;

        if (defaultInsideColor) {
            this.productDataService.updateSelectedOptionByName('mullionColorInner', defaultInsideColor.value);
            this.uiService.checkOptionInSelect(
                this.uiService.getElementByAttribute('nf-template-action', 'muntinInsideColorSelect'),
                defaultInsideColor.value
            );
        }
        if (defaultOutsideColor) {
            this.productDataService.updateSelectedOptionByName('mullionColorOuter', defaultOutsideColor.value);
            this.uiService.checkOptionInSelect(
                this.uiService.getElementByAttribute('nf-template-action', 'muntinOutsideColorSelect'),
                defaultOutsideColor.value
            );
        }
        if (defaultInsideColor && defaultOutsideColor) {
            this.uiService.displayTopNotification('muntinColorChangedNotice');
        } else {
            this.uiService.displayTopNotification('muntinColorDidntChangeNotice');
        }

        this.eventService.emit('reloadDrawing');
    }

    private checkPvcWindowMullionRule() {
        const blockMullions = this.mullionsService.validatePvcWindowMullionRule();
        let setMullionSize = false;

        const mullions = this.uiService.getElementsByPartName('mullion');
        mullions.forEach((mullion) => {
            const mullionSize = mullion.getAttribute('nf-option-size');

            if (blockMullions && mullionSize === this.pvcWindowMullionRule.getMullionSizeRestriction()) {
                this.uiService.disableOption(mullion);

                if (this.productDataStore.getSelectedOptionIdByName('dividerSize') === +mullion.getAttribute('nf-option-id')) {
                    setMullionSize = true;
                }
            } else {
                this.uiService.enableOption(mullion);
            }
        });

        if (setMullionSize) {
            this.uiService.resetSelectionsByPartName('mullion');
            const newMullion = this.productDataStore.getAvailableOptionsByName('dividerSize').find(option => option.attributes.size !== this.pvcWindowMullionRule.getMullionSizeRestriction());

            this.productDataService.updateSelectedOptionByName('dividerSize', newMullion.id);
            this.uiService.setSelectedOption(this.uiService.getElementByPartNameAndId('mullion', newMullion.id));
        }
    }
}
